import log from 'loglevel';

export function resolveLoggerLevel(): log.LogLevelDesc {
  const configured = import.meta.env.VITE_LOG_LEVEL;
  if (configured) {
    return configured as log.LogLevelDesc;
  }
  return resolveDefaultLoggerLevel();
}

export function resolveDefaultLoggerLevel(
  isDevelopment: boolean = import.meta.env.DEV,
): log.LogLevelDesc {
  return (isDevelopment ? 'debug' : 'warn') as log.LogLevelDesc;
}

// Configure log level based on environment
const logLevel = resolveLoggerLevel();

log.setLevel(logLevel);

// Custom log formatting for browser console
const originalFactory = log.methodFactory;
log.methodFactory = function (methodName, logLevel, loggerName) {
  const rawMethod = originalFactory(methodName, logLevel, loggerName);

  return function (message: unknown, ...args: unknown[]) {
    const timestamp = new Date().toISOString();
    const level = methodName.toUpperCase();
    const prefix = `[${timestamp}] [${level}]`;
    
    // Add styling for different log levels
    const styles: Record<string, string> = {
      ERROR: 'color: #ff4444; font-weight: bold;',
      WARN: 'color: #ffaa00; font-weight: bold;',
      INFO: 'color: #0088cc; font-weight: bold;',
      DEBUG: 'color: #888888;',
    };

    const style = styles[level] || '';

    if (typeof message === 'string') {
      rawMethod(`%c${prefix} ${message}`, style, ...args);
    } else {
      rawMethod(`%c${prefix}`, style, message, ...args);
    }
  };
};

// Re-initialize with new method factory
log.setLevel(log.getLevel());

// Extended logger interface with structured logging methods
interface ExtendedLogger extends log.Logger {
  // User interactions
  userAction: (action: string, details?: Record<string, unknown>) => void;
  
  // Component lifecycle
  componentMount: (componentName: string, props?: Record<string, unknown>) => void;
  componentUnmount: (componentName: string) => void;
  componentError: (componentName: string, error: Error, errorInfo?: unknown) => void;
  
  // Network requests
  apiRequest: (method: string, url: string, data?: unknown) => void;
  apiResponse: (method: string, url: string, status: number, duration?: number) => void;
  apiError: (method: string, url: string, error: Error | string, status?: number) => void;
  
  // Authentication events
  loginAttempt: (email: string) => void;
  loginSuccess: (userId: string, tenantId?: string) => void;
  loginFailure: (reason: string) => void;
  logout: (userId?: string) => void;
  
  // Security events
  securityEvent: (event: string, details?: Record<string, unknown>) => void;
  
  // Performance monitoring
  performanceMetric: (metric: string, value: number, unit?: string) => void;
  slowOperation: (operation: string, duration: number, threshold?: number) => void;
  
  // UI/UX events
  pageView: (path: string, title?: string) => void;
  formSubmit: (formName: string, success: boolean, errors?: string[]) => void;
  buttonClick: (buttonName: string, location?: string) => void;
  
  // Data events
  dataLoad: (dataType: string, count?: number, source?: string) => void;
  dataError: (dataType: string, error: Error | string) => void;
  
  // Theme and preferences
  themeChange: (from: string, to: string) => void;
  settingsChange: (setting: string, value: unknown) => void;
}

// Create extended logger
const logger = log as unknown as ExtendedLogger;

// Implement extended logging methods
logger.userAction = (action: string, details: Record<string, unknown> = {}) => {
  logger.info('User Action', { action, ...details, timestamp: Date.now() });
};

logger.componentMount = (componentName: string, props: Record<string, unknown> = {}) => {
  logger.debug('Component Mount', { component: componentName, props, timestamp: Date.now() });
};

logger.componentUnmount = (componentName: string) => {
  logger.debug('Component Unmount', { component: componentName, timestamp: Date.now() });
};

logger.componentError = (componentName: string, error: Error, errorInfo?: unknown) => {
  logger.error('Component Error', {
    component: componentName,
    error: error.message,
    stack: error.stack,
    errorInfo,
    timestamp: Date.now(),
  });
};

logger.apiRequest = (method: string, url: string, data?: unknown) => {
  logger.debug('API Request', {
    method,
    url,
    data: data ? (typeof data === 'object' ? JSON.stringify(data) : data) : undefined,
    timestamp: Date.now(),
  });
};

logger.apiResponse = (method: string, url: string, status: number, duration?: number) => {
  const level = status >= 400 ? 'warn' : 'debug';
  logger[level]('API Response', {
    method,
    url,
    status,
    duration: duration ? `${duration}ms` : undefined,
    timestamp: Date.now(),
  });
};

logger.apiError = (method: string, url: string, error: Error | string, status?: number) => {
  logger.error('API Error', {
    method,
    url,
    error: typeof error === 'string' ? error : error.message,
    stack: typeof error === 'object' ? error.stack : undefined,
    status,
    timestamp: Date.now(),
  });
};

logger.loginAttempt = (email: string) => {
  logger.info('Login Attempt', { email, timestamp: Date.now() });
};

logger.loginSuccess = (userId: string, tenantId?: string) => {
  logger.info('Login Success', { userId, tenantId, timestamp: Date.now() });
};

logger.loginFailure = (reason: string) => {
  logger.warn('Login Failure', { reason, timestamp: Date.now() });
};

logger.logout = (userId?: string) => {
  logger.info('Logout', { userId, timestamp: Date.now() });
};

logger.securityEvent = (event: string, details: Record<string, unknown> = {}) => {
  logger.warn('Security Event', { event, ...details, timestamp: Date.now() });
};

logger.performanceMetric = (metric: string, value: number, unit: string = 'ms') => {
  logger.debug('Performance Metric', { metric, value, unit, timestamp: Date.now() });
};

logger.slowOperation = (operation: string, duration: number, threshold: number = 1000) => {
  if (duration > threshold) {
    logger.warn('Slow Operation', {
      operation,
      duration: `${duration}ms`,
      threshold: `${threshold}ms`,
      timestamp: Date.now(),
    });
  }
};

logger.pageView = (path: string, title?: string) => {
  logger.info('Page View', { path, title, timestamp: Date.now() });
};

logger.formSubmit = (formName: string, success: boolean, errors?: string[]) => {
  const level = success ? 'info' : 'warn';
  logger[level]('Form Submit', {
    formName,
    success,
    errors,
    timestamp: Date.now(),
  });
};

logger.buttonClick = (buttonName: string, location?: string) => {
  logger.debug('Button Click', { buttonName, location, timestamp: Date.now() });
};

logger.dataLoad = (dataType: string, count?: number, source?: string) => {
  logger.debug('Data Load', { dataType, count, source, timestamp: Date.now() });
};

logger.dataError = (dataType: string, error: Error | string) => {
  logger.error('Data Error', {
    dataType,
    error: typeof error === 'string' ? error : error.message,
    stack: typeof error === 'object' ? error.stack : undefined,
    timestamp: Date.now(),
  });
};

logger.themeChange = (from: string, to: string) => {
  logger.info('Theme Change', { from, to, timestamp: Date.now() });
};

logger.settingsChange = (setting: string, value: unknown) => {
  logger.info('Settings Change', { setting, value, timestamp: Date.now() });
};

// Global error handler for unhandled errors
window.addEventListener('error', (event) => {
  logger.error('Unhandled Error', {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    stack: event.error?.stack,
    timestamp: Date.now(),
  });
});

// Global handler for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  logger.error('Unhandled Promise Rejection', {
    reason: event.reason?.toString(),
    stack: event.reason?.stack,
    timestamp: Date.now(),
  });
});

// Export the extended logger
export default logger;
